import React from "react";
import { useTranslation } from "react-i18next";

// Building block for loading placeholders. Composes the `.skeleton` shimmer
// from styles/animations.css, which is based on the theme surface variables
// and therefore adapts to dark mode automatically.
export const Skeleton: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div aria-hidden="true" className={`skeleton rounded ${className}`} />
);

export const SkeletonText: React.FC<{ lines?: number; className?: string }> = ({
  lines = 3,
  className = "",
}) => (
  <div aria-hidden="true" className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }, (_, i) => (
      <Skeleton key={i} className={`h-3 ${i === lines - 1 ? "w-2/3" : "w-full"}`} />
    ))}
  </div>
);

// Mirrors the layout of ReportListItem (header row with username/emoji and
// date, then a few lines of comment) to minimize layout shift.
export const SkeletonReportCard: React.FC = () => (
  <div
    aria-hidden="true"
    className="p-4 bg-gray-100/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50"
  >
    <div className="flex justify-between items-center mb-3">
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-6 w-6 rounded-full" />
      </div>
      <Skeleton className="h-3 w-14" />
    </div>
    <SkeletonText lines={3} />
  </div>
);

// Accessible wrapper for a group of skeletons: announced as busy/loading to
// screen readers while the visual placeholders stay aria-hidden.
export const SkeletonGroup: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = "",
}) => {
  const { t } = useTranslation();
  return (
    <div role="status" aria-busy="true" aria-live="polite" className={className}>
      <span className="sr-only">{t("restaurant.loadingDetails")}</span>
      {children}
    </div>
  );
};
